import React from "react";
import { Shield, Lock, FileText } from "lucide-react";
import Footer from "../../components/Footer";

const PrivacyPolicyPage = () => {
  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header Section */}
      <div className="relative py-16 bg-green-600">
        <div className="absolute inset-0 bg-opacity-75 bg-black"></div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center mb-6">
            <Lock className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">
            WeRide Privacy Policy
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            At WeRide, we are committed to protecting your personal information
            and ensuring that your experience with our ride-hailing app is safe
            and secure.
          </p>
        </div>
      </div>

      {/* Privacy Policy Content */}
      <div className="max-w-4xl mx-auto px-4 py-12 bg-white shadow-md rounded-lg -mt-8 mb-12">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <p className="text-gray-600">Effective Date: 26 March 2025</p>
            <p className="text-gray-600">Last Updated: 26 March 2025</p>
          </div>
          <button
            onClick={() => {
              window.print();
            }}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition-all duration-300"
          >
            <FileText className="w-4 h-4 mr-2" />
            Download PDF
          </button>
        </div>

        {/* Section 1 */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-green-600 mb-4">
            1. Introduction
          </h2>
          <p className="text-gray-700 leading-relaxed">
            This Privacy Policy explains how WeRide collects, uses, discloses,
            and protects your personal information when you use our app,
            website, or services. By using WeRide, you agree to the terms of
            this Privacy Policy.
          </p>
        </div>

        {/* Section 2 */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-green-600 mb-4">
            2. Collection of Personal Information
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            WeRide collects personal information from you when you:
          </p>
          <ul className="list-disc pl-6 mb-4 text-gray-700 leading-relaxed">
            <li>Create an account with us</li>
            <li>Use our app or website</li>
            <li>Request a ride</li>
            <li>Contact our customer support team</li>
            <li>Participate in surveys or promotions</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mb-4">
            The types of personal information we collect may include:
          </p>
          <ul className="list-disc pl-6 text-gray-700 leading-relaxed">
            <li>Name</li>
            <li>Email address</li>
            <li>Phone number</li>
            <li>Password</li>
            <li>Payment information (e.g., credit card details)</li>
            <li>Location data (e.g., GPS coordinates)</li>
            <li>Ride history and preferences</li>
            <li>Device information (e.g., operating system, browser type)</li>
          </ul>
        </div>

        {/* Section 3 */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-green-600 mb-4">
            3. Use of Personal Information
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            WeRide uses your personal information for the following purposes:
          </p>
          <ul className="list-disc pl-6 text-gray-700 leading-relaxed">
            <li>To provide and improve our services</li>
            <li>To facilitate ride requests and payments</li>
            <li>
              To communicate with you about your account, rides, and promotions
            </li>
            <li>To analyze usage patterns and improve the app's performance</li>
            <li>To comply with legal requirements and regulations</li>
          </ul>
        </div>

        {/* Section 4 */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-green-600 mb-4">
            4. Disclosure of Personal Information
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            WeRide may disclose your personal information to:
          </p>
          <ul className="list-disc pl-6 text-gray-700 leading-relaxed">
            <li>Our affiliates and subsidiaries</li>
            <li>
              Our service providers (e.g., payment processors, mapping services)
            </li>
            <li>Law enforcement agencies or regulatory authorities</li>
            <li>
              Other third parties with your consent or as permitted by law
            </li>
          </ul>
        </div>

        {/* Section 5 */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-green-600 mb-4">
            5. Data Security
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            WeRide takes reasonable measures to protect your personal
            information from unauthorized access, disclosure, alteration, or
            destruction. These measures include:
          </p>
          <ul className="list-disc pl-6 text-gray-700 leading-relaxed">
            <li>Encryption of sensitive data</li>
            <li>Secure socket layer (SSL) technology</li>
            <li>Firewalls and intrusion detection systems</li>
            <li>Access controls and authentication procedures</li>
          </ul>
        </div>

        {/* Section 6 */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-green-600 mb-4">
            6. Data Retention
          </h2>
          <p className="text-gray-700 leading-relaxed">
            WeRide retains your personal information for as long as necessary to
            provide our services, comply with legal requirements, and resolve
            disputes. We may also retain aggregated or anonymized data for
            analytical purposes.
          </p>
        </div>

        {/* Section 7 */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-green-600 mb-4">
            7. Your Rights
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            You have the right to:
          </p>
          <ul className="list-disc pl-6 text-gray-700 leading-relaxed">
            <li>Access and correct your personal information</li>
            <li>Object to the processing of your personal information</li>
            <li>Request deletion of your personal information</li>
            <li>Opt-out of marketing communications</li>
          </ul>
        </div>

        {/* Section 8 */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-green-600 mb-4">
            8. Changes to this Privacy Policy
          </h2>
          <p className="text-gray-700 leading-relaxed">
            WeRide may update this Privacy Policy from time to time. We will
            notify you of any significant changes by email or through the app.
          </p>
        </div>

        {/* Section 9 */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-green-600 mb-4">
            9. Contact Us
          </h2>
          <p className="text-gray-700 leading-relaxed">
            If you have any questions or concerns about this Privacy Policy or
            our handling of your personal information, please contact us at{" "}
            <a
              href="mailto:support@weride.club"
              className="text-green-600 hover:underline"
            >
              support@weride.club
            </a>
          </p>
        </div>

        {/* Governing Law */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-green-600 mb-4">
            Governing Law
          </h2>
          <p className="text-gray-700 leading-relaxed">
            This Privacy Policy shall be governed by and construed in accordance
            with the laws of South Africa. Any disputes arising out of or
            related to this Privacy Policy shall be resolved through Alternative
            Dispute Resolution (ADR).
          </p>
        </div>

        {/* Agreement */}
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mt-12">
          <p className="text-gray-700 text-center">
            By using WeRide, you acknowledge that you have read, understood, and
            agree to be bound by this Privacy Policy.
          </p>
        </div>
      </div>

      {/* Trust Features */}
      <div className="max-w-6xl mx-auto px-4 mb-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-black">
            Your Privacy Matters To Us
          </h2>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
            At WeRide, we implement industry-leading security measures to keep
            your data safe and protected.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <div className="bg-green-50 p-4 rounded-full inline-flex mb-6">
              <Shield className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-black mb-4">
              Data Protection
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Your personal information is encrypted and stored securely with
              industry-standard protocols.
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <div className="bg-green-50 p-4 rounded-full inline-flex mb-6">
              <Lock className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-black mb-4">
              Secure Transactions
            </h3>
            <p className="text-gray-600 leading-relaxed">
              All payment information is processed through secure, PCI-compliant
              payment gateways.
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <div className="bg-green-50 p-4 rounded-full inline-flex mb-6">
              <FileText className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-black mb-4">
              Transparent Policies
            </h3>
            <p className="text-gray-600 leading-relaxed">
              We're committed to clear communication about how we collect and
              use your information.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
